/**
 * Holds the browser's install prompt so the app can show its own "Install app" buttons.
 * The browser only offers the prompt when its install rules are met, so the state can say "not offered yet".
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** A string, not an object, so it can be used directly as a stable React snapshot. */
export type InstallKind = 'installed' | 'available' | 'ios' | 'not-offered';

let deferred: BeforeInstallPromptEvent | null = null;
let installedNow = false;
let captured = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

/* Remembered "Not now": the in-app install card stays away for a while after it is dismissed. */
const DISMISS_KEY = 'v2.install.dismissedAt';
let sessionDismissed = false;
export const INSTALL_DISMISS_DAYS = 30;
const DISMISS_MS = INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000;

function readDismissedAt(): number {
  try {
    const value = Number(window.localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

/** True while a dismissal of the install card is still fresh. */
export function isInstallDismissed(now = Date.now()): boolean {
  const at = readDismissedAt();
  return at > 0 && now - at < DISMISS_MS;
}

/** Remembers "Not now" (or a cancelled browser dialog) on this device. */
export function dismissInstall(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Storage can be disabled; the card then simply stays hidden for this session.
  }
  sessionDismissed = true;
  emit();
}

/** Snapshot for the in-app install card: the kind to offer, or 'hidden'. */
export type InstallCardKind = 'available' | 'ios' | 'hidden';
export function getInstallCardKind(): InstallCardKind {
  const kind = getInstallKind();
  if (sessionDismissed || isInstallDismissed()) return 'hidden';
  if (kind === 'available') return 'available';
  if (kind === 'ios' && isIosSafari()) return 'ios';
  return 'hidden';
}

/** Safari itself (not an in-app browser), where Share → Add to Home Screen exists. */
export function isIosSafari(): boolean {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent;
  return /Safari\//.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line)\//.test(ua);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  // Any installed display mode counts (the manifest asks for standalone, with minimal-ui as fallback).
  const installedModes = ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'];
  return installedModes.some((mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true) || nav.standalone === true;
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Starts listening for the browser prompt. Idempotent. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installedNow = true;
    emit();
  });
  // Opening the installed app (or leaving it) changes the display mode without a reload.
  try {
    ['standalone', 'minimal-ui'].forEach((mode) => {
      window.matchMedia?.(`(display-mode: ${mode})`)?.addEventListener?.('change', () => emit());
    });
  } catch {
    // Old browsers without matchMedia listeners: the state is read again on the next render.
  }
}

export function getInstallKind(): InstallKind {
  if (installedNow || isStandalone()) return 'installed';
  if (deferred) return 'available';
  if (isIosDevice()) return 'ios';
  return 'not-offered';
}

/** Shows the browser install dialog. Resolves to the user's choice, or null when no prompt is held. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferred) return null;
  const event = deferred;
  deferred = null;
  emit();
  try {
    await event.prompt();
    const choice = await event.userChoice;
    // Cancelling the browser dialog counts as "Not now" for the in-app card.
    if (choice.outcome === 'dismissed') dismissInstall();
    return choice.outcome;
  } catch {
    return null;
  }
}

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
